import { ApplicantTable, ApplicantCreatorButton, TableButton, TableWhen, Table, interfaceNumber, NotificationSender, TargetFieldRef, SubsurveyRef, ColumnRenderOptions } from "@aidkitorg/types/lib/survey";
import { buildLinkGensByCol, type LinkTemplateGenerator } from "../utils/dashboardUtils";
import { Fragment, memo, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePost } from "../API";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/20/solid";
import InterfaceContext, { OfflineSyncContext, PublicConfigurationContext } from "../Context";
import { copyToClipboard, languageContent, RigidSpinner, snakeToEnglish } from "../Util";
import { useLocalizedStrings, useLocalizer } from "../Localization";
import SyncBadge from "../Components/SyncBadge";
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import { Dialog, Transition } from "@headlessui/react";
import { Button, Icon, useToast } from "@aidkitorg/component-library";
import { ZoomableImage } from "../Components/TurnableImage";
import { Link } from "react-router-dom";
import { useCreateAssociatedApplicant } from "../Questions/ApplicantCreator";
import { isAidKitDocumentsUrl, useSignedS3Urls } from "../utils/signedS3Urls";

const numberTestRegex = new RegExp(/^-?\d+(?:\.\d+)?$/);

export interface FormattedColumnValue {
  content: string | JSX.Element[];
  JSONContent: Object | undefined;
  shouldTreatContentAsJson: boolean;
}

/**
 * Formats a column value for display in a table.
 * Handles JSON parsing, object stringification, and special cases like aidkit-documents URLs.
 * 
 * @param value - The raw column value to format
 * @param createZoomableImage - Optional function to create ZoomableImage components for aidkit-documents URLs
 * @returns Formatted column value with metadata
 */
export function formatColumnValue(
  value: any,
  createZoomableImage?: (url: string, index: number) => JSX.Element,
  renderOptions?: ColumnRenderOptions['renderType']
): FormattedColumnValue {
  const shouldTreatContentAsJson = typeof value === 'object' && value !== null;
  let content: string | JSX.Element[] = shouldTreatContentAsJson ? JSON.stringify(value) : String(value ?? '');
  let JSONContent: Object | undefined;
  
  try {
    // Parsing strings into numbers can have unintended consequences like removing trailing 0s
    JSONContent = (typeof content === 'string' && !numberTestRegex.test(content)) ? JSON.parse(content) : undefined;
  } catch (e) {
    JSONContent = undefined;
  }

  if (!(shouldTreatContentAsJson || JSONContent)) {
    if (isAidKitDocumentsUrl(content) && createZoomableImage) {
      content = content.split(',').map((url: string, index: number) => createZoomableImage(url, index));
    } else if (renderOptions?.kind === 'Column As Link' && typeof content === 'string' && content.toLowerCase().startsWith('http')) {
      // AidKit documents are a very special case, and rendering them as ordinary links would always fail,
      // so we apply custom render options after the AidKit document-specific logic, not before.
      content = content.trim();
      content = [<a href={content as string} target='_blank'>{renderOptions.label ?? content}</a>];
    } else {
      // Fallback: if no createZoomableImage function provided, keep as string
      // This maintains backward compatibility
    }
  }

  return {
    content,
    JSONContent,
    shouldTreatContentAsJson
  };
}


function NotifSendButtonComponent(props: { col: string, uid: string, notifTargetField: TargetFieldRef, prev: any }): JSX.Element {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<'unsent' | 'success' | 'failure'>(!props.prev ? 'unsent' : ((props.prev as string).startsWith('error') ? 'failure' : 'success'));
  const issueNotif = usePost('/program/admin/issue_notification');

  const notifType = props.notifTargetField.endsWith('_sms') ? 'sms' : 'email';

  async function sendNotification() {

    // Using a callback here guarantees that multiple clicks will not cause multiple API calls
    // because React automatically queues the set requests and executes them sequentially.
    setSending((currentSending) => {
      if (currentSending) {
        console.log("Already clicked");
        return currentSending;
      }
      (async () => {
        try {
          const fullNotifTargetField = props.notifTargetField;
          const res = await issueNotif({
            token_kind: 'auth',
            target_field: fullNotifTargetField,
            applicant: props.uid,
          });


          if ("error" in res || res[fullNotifTargetField]?.errors > 0) {
            setResult('failure');
          } else if (res[fullNotifTargetField]?.successes > 0) {
            setResult('success');
          } else {
            setResult('failure');
            console.error('Error: unexpected result from issuing notif');
          }

        } catch (error) {
          console.error("Error in issueNotif", error);
          setResult('failure');
        } finally {
          setSending(false);
        }
      })();

      return true;
    });
  }

  return <td key={props.col} className="px-3 py-4 text-sm text-gray-500 max-w-md break-word">
    {sending
      ? <RigidSpinner />
      : (result === 'unsent'
        ? <button onClick={sendNotification} disabled={sending} className='inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1.5 my-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'>
          {'Send ' + notifType + ' notif!'}
        </button>
        : result === 'success'
          ? 'Sent!' : 'Sending failed.'
      )
    }
  </td>;
};

function ApplicantCreatorButtonComponent(props: { uid: string, applicantCreatorTargetField: TargetFieldRef, existingLinkedUid: string | undefined, label: string, linkTextPrefix?: string }): JSX.Element {
  const [createdUid, setCreatedUid] = useState<string | undefined>(undefined);
  const linkedUid = createdUid ?? props.existingLinkedUid;
  const { toast } = useToast();
  const { creating, create } = useCreateAssociatedApplicant({
    uid: props.uid,
    targetField: props.applicantCreatorTargetField,
    onSuccess: (newId) => setCreatedUid(newId),
    onError: (error) => toast({ description: error, variant: 'error' }),
  });

  return <td key={props.applicantCreatorTargetField} className="px-3 py-4 text-sm text-gray-500 max-w-md break-word">
    {creating
      ? <RigidSpinner />
      : (linkedUid
        ? <Link to={`/a/${linkedUid}`} target="_blank" className="text-indigo-600 hover:text-indigo-900">
          {props.linkTextPrefix ? `${props.linkTextPrefix}${linkedUid}` : linkedUid}
        </Link>
        : <Button variant='secondary' onClick={create} size='sm'>
          {props.label}
        </Button>
      )
    }
  </td>;
}

function TableWhenCell(props: {
  col: TableWhen,
  cellValue: string,
  uid: string,
  content: string | JSX.Element[],
  JSONContent: any,
  cellRenderer?: (v: any) => ReactNode,
  localize: ReturnType<typeof useLocalizer>,
}): JSX.Element {
  const { col, cellValue, uid, content, JSONContent, cellRenderer, localize } = props;
  try {
    const { button, targetField, buttonMetadata } = JSON.parse(cellValue);
    if (button === 'ApplicantCreatorButton') {
      const btn = [...col.when.map(b => b.then), col.otherwise]
        .filter((t): t is TableButton => t?.kind === 'Table Button')
        .find(t => t.button.kind === 'ApplicantCreatorButton' && t.button.applicantCreatorTargetField === targetField)
        ?.button as ApplicantCreatorButton | undefined;
      if (btn) return <ApplicantCreatorButtonComponent
        uid={uid}
        applicantCreatorTargetField={targetField}
        existingLinkedUid={buttonMetadata}
        label={localize(btn.label) ?? 'Create Applicant'}
        linkTextPrefix={localize(btn.linkTextPrefix) ?? undefined}
      />;
    }
  } catch {}
  if (!content) return <td className="px-3 py-4 text-sm text-gray-500" />;
  return <td className="px-3 py-4 text-sm text-gray-600 max-w-md break-words">
    {cellRenderer ? cellRenderer(cellValue) : JSONContent ? <pre className="">{JSON.stringify(JSONContent, undefined, 2)}</pre> : <span>{content}</span>}
  </td>;
}

export function DashboardTableSearch(props: {
  currFilter: Record<string, string>,
  open: boolean, 
  close: (filters: Record<string, string> | undefined, doSearch?: boolean) => void, 
  cols: string[]
}) {
  const L = useLocalizedStrings();
  const [filter, setFilter] = useState<Record<string, string>>({});
  const handleFilter = useCallback((columnKey: string, columnValue: string) => {
    if (columnValue.trim()) {
      setFilter({
        ...filter,
        [columnKey]: columnValue
      });
    } else {
      setFilter((prevFilter) => {
        const { [columnKey]: _, ...rest } = prevFilter;
        return rest;
      });
    }
  }, [filter]);

  useEffect(() => {
    setFilter(props.currFilter);
  }, [props.currFilter]);

  return (
    <Transition.Root show={props.open} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 overflow-y-auto" 
        style={{ zIndex: 20000 }}
        onClose={() => props.close(filter)}>
        <div className="flex items-start justify-center min-h-screen pt-5 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div 
              className="relative md:w-1/2 w-full h-full overflow-y-auto inline-block align-bottom bg-white rounded-lg px-4 pt-4 pb-4 text-left shadow-xl transform transition-all">
              <div className="text-xl font-semibold" >{L.dashboard.filter_table_results}</div>
              <div className="flex flex-wrap justify-between mt-4">
                {props.cols.map(c => {
                  if (c === 'uid') {
                    return;
                  }
                  return (
                    <div key={c} className="w-full sm:w-1/2 lg:w-1/3">
                      <div className="px-1">
                        <label htmlFor={c} className="block text-sm font-medium leading-6 text-gray-900">
                          {snakeToEnglish(c)}
                        </label>
                        <input 
                          value={filter[c] || ''}
                          id={c}
                          onChange={(e) => {
                            handleFilter(c, e.target.value);
                          }}
                          className="max-w-lg block w-full shadow-sm border-solid p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md border-2 border-gray-200 focus:ring-gray-400 ring-gray-400 focus:border-gray-400" 
                        />
                      </div>
                    </div>);
                })}
              </div>
              <button
                className={`shadow rounded-md border border-transparent px-4 py-2 mt-4 float-right text-sm font-medium shadow-sm bg-indigo-600 text-white 
                                    focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600`}
                onClick={() => { props.close(filter, true) }}
              >
                {L.dashboard.apply_filters}
              </button>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export type DistroDashboardTableProps = {
  records: Record<string, any>[] | undefined,
  component: ApplicantTable | Table,
  extra?: ReactNode,
  dashboardLinksToSection?: string | SubsurveyRef,
  title?: string,
  count?: number,
  queryExecutionTimeMs?: number,
  lastUpdatedAt?: number,
  cellRenderer?(content: string | Object): React.ReactNode,
  onFilterChange?: (filter: Record<string, any>) => void,
  onSortChange?: (sort: { key: string; order: 'ascending' | 'descending' } | undefined) => void,
};

export function DistroDashboardTable(props: DistroDashboardTableProps) {
  const L = useLocalizedStrings();
  const localize = useLocalizer();
  const config = useContext(PublicConfigurationContext);
  const interfaceContext = useContext(InterfaceContext);
  const offlineSync = useContext(OfflineSyncContext);
  const cols = Object.keys(props.records?.[0] || {});
  const notifColumns: string[] = [];
  const applicantCreatorButtonColumns = new Map<string, ApplicantCreatorButton>();
  const tableWhenColumns = new Map<string, TableWhen>();
  // LinkTo column metadata, keyed by SQL column name. Each generator has a
  // precomputed `templateRegex` so the cell renderer can pick the right
  // `When`-branch label per row by matching the row's URL value, without
  // depending on a `uid` field. Shared with ReportViewPage and
  // PrecomputedChartCard via `buildLinkGensByCol`.
  const linkGensByCol: Record<string, LinkTemplateGenerator[]> =
    props.component.kind === 'Applicant Table'
      ? buildLinkGensByCol(props.component.columns)
      : {};

  const cellRenderer = props.cellRenderer;
  let offlineStatusCols: string[] = [];
  const noHeaderCols: string[] = [];

  if (props.component.kind === 'Applicant Table') {
    if (props.component.showUids) {
      cols.splice(1, 0, 'full_uid');
    }

    props.component.columns.filter((col) => col.kind === 'NotificationSender').forEach((notifCol) => {
      notifColumns.push((notifCol as NotificationSender).field + '_sms');
      notifColumns.push((notifCol as NotificationSender).field + '_email');
    });

    props.component.columns.filter((col) => col.kind === 'ApplicantCreatorButton').forEach((col) => {
      const acbCol = col as ApplicantCreatorButton;
      if (acbCol.applicantCreatorTargetField) {
        applicantCreatorButtonColumns.set(acbCol.applicantCreatorTargetField, acbCol);
      }
    });

    props.component.columns.filter((col) => col.kind === 'Table When').forEach((col) => {
      const tableWhenCol = col as TableWhen;
      tableWhenColumns.set(tableWhenCol.name, tableWhenCol);
    });

    // Apply LinkTo column metadata: hide headers when requested, and add
    // synthetic columns for any LinkTo column the SQL data didn't already
    // include (typically Custom Query results).
    for (const colName of Object.keys(linkGensByCol)) {
      if (linkGensByCol[colName].some((g) => g.hideHeader)) {
        noHeaderCols.push(colName);
      }
      if (!cols.includes(colName)) {
        cols.push(colName);
      }
    }

    offlineStatusCols = props.component.columns
      .filter(c => c.kind === 'Sync Status' || c.kind === 'Named Expression' && c.expression.kind === 'Sync Status')
      .map(c => c.kind === 'Named Expression' ? c.name?.en ?? c.expression.kind : c.kind)
      .map(c => c.toString().toLowerCase());
  }

  const superHeader = !!(props.title || props.count);
  const context = useContext(PublicConfigurationContext);
  const appPath = interfaceNumber(context?.interface?.version) >= 1 ? '/a/' : '/applicant/';

  const createPdf = usePost('/document/create_pdf');
  const [uidDownloadingPdf, setUidDownloadingPdf] = useState<string>('');
  const [pdfDownloadLabel, setPdfDownloadLabel] = useState<string>('');

  const [openSearch, setOpenSearch] = useState(false);
  const [filter, setFilter] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<{ key: string, order: 'ascending' | 'descending'} | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [currentPageRecords, setCurrentPageRecords] = useState<Record<string, any>[]>([]);
  const [totalPages, setTotalPages] = useState(1);

  const currentPageS3Urls = useMemo(() => {
    const currentPageValues = currentPageRecords.flatMap((record) => Object.values(record));
    // Urls could be `url` or `url1,url2,url3`
    const currentPageS3UrlsConcatenated = currentPageValues.filter(isAidKitDocumentsUrl);
    return currentPageS3UrlsConcatenated.flatMap((s3Url) => s3Url.split(','));
  }, [currentPageRecords]);

  const signedS3Urls = useSignedS3Urls(currentPageS3Urls);

  const { toast } = useToast();

  // Update parent component when filter/sort changes
  useEffect(() => {
    if (props.onFilterChange) {
      props.onFilterChange(filter);
    }
  }, [filter]);

  useEffect(() => {
    if (props.onSortChange) {
      props.onSortChange(sort);
    }
  }, [sort]);

  useEffect(() => {
    setCurrentPage(1);
  }, [props.records]);

  useEffect(() => {
    const sortedFilteredRecords = props.records || [];
    if (props.component.showAllSimultaneously) {
      setCurrentPageRecords(sortedFilteredRecords);
      setTotalPages(1);
      return;
    }

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setCurrentPageRecords(sortedFilteredRecords.slice(startIndex, endIndex));
    setTotalPages(Math.ceil(sortedFilteredRecords.length / itemsPerPage));
  }, [props.records, currentPage, props.component.showAllSimultaneously]);

  function handleSort(columnKey: string) {
    if (sort?.key === columnKey) {
      setSort({ key: columnKey, order: sort.order === 'ascending' ? 'descending' : 'ascending' });
    } else {
      setSort({ key: columnKey, order: 'ascending' });
    }
  }

  const [resolvedLinksToSection, setResolvedLinksToSection] = useState<string | SubsurveyRef | undefined>(undefined);
  const [resolvedLinkLabel, setResolvedLinkLabel] = useState<string | undefined>(undefined);

  useEffect(() => {
    // Resolve the linksToSection variable with hierarchical fallback: table -> dashboard -> default
    if (props.component.linksToSection) {
      setResolvedLinksToSection(props.component.linksToSection.subsurvey);

      if (props.component.linksToSection.label) {
        setResolvedLinkLabel(localize(props.component.linksToSection.label) || undefined);
      }
    } else if (props.dashboardLinksToSection) {
      setResolvedLinksToSection(props.dashboardLinksToSection);
    }
  }, [props.component.linksToSection, props.dashboardLinksToSection, localize]);

  useEffect(() => {
    // Set resolvedPdfDownloadLabel for the PDF download button
    if (props.component.pdfDownload) {
      let label = L.dashboard.download_as_pdf.replace('$subsurvey', props.component.pdfDownload.subsurvey.path);
      if (props.component.pdfDownload.label) {
        label = localize(props.component.pdfDownload.label) ?? label;
      }
      setPdfDownloadLabel(label);
    }
  }, [props.component.pdfDownload, localize]);

  // Hide View button if specified in Distro or if query doesn't include any UIDs
  const hideViewButton = props.component.hideViewButton || (!cols.includes('uid') && !cols.includes('applicant'));
  const includePdfDownload = !!props.component.pdfDownload;
  const columnToRenderOptionsMap = useMemo(() => {
    const map: Record<string, ColumnRenderOptions['renderType']> = {};
    if (props.component.kind === 'Table' && props.component.columnRenderOptions) {
      props.component.columnRenderOptions.forEach((opt) => {
        map[opt.column.toLowerCase()] = opt.renderType;
      });
    }
    return map;
  }, [props.component]);

  return <OfflineSyncContext.Provider value={offlineSync}>
    <div className="clear-both">
      <div className="overflow-x-auto shadow">
        <div className="inline-block min-w-full align-middle">
          <div className="overflow-hidden ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <DashboardTableSearch 
              currFilter={filter}
              open={openSearch} 
              close={(newFilters: Record<string, string> | undefined, doSearch?: boolean) => { 
                if (doSearch) {
                  setFilter({...newFilters});
                } else {
                  // We do this to make sure currFilter updates the next time we open the modal.
                  // This makes sure the filters on open match what is currently set.
                  setFilter({...filter})
                }
                setOpenSearch(false);
              }} 
              cols={cols} />

            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                {/* Ternary needed here because of React's handling of conditionals https://stackoverflow.com/a/53519842 */}
                {(superHeader || !props.records?.length) ? (<tr className="border-[0] border-solid border-b-2 border-gray-300">
                  <th
                    scope="col"
                    colSpan={cols.length + (hideViewButton ? 0 : 1) + (includePdfDownload ? 1 : 0)}
                    className="py-3.5 px-3 text-left text-base font-semibold text-gray-900 sm:pl-6"
                  >
                    <div className="flex justify-between items-center"> 
                      <div className="flex items-center space-x-2">
                        {(config.experimental?.enableOfflineMode && props.component.kind === 'Applicant Table') && 
                          <SyncBadge 
                            id={props.records?.map(r => r.uid)}
                            override={<ArrowDownTrayIcon height={15} width={15} />} 
                          />}
                        {props.extra}
                        {props.title || ''}
                        {props.queryExecutionTimeMs && (
                          <span className='text-gray-500 text-xs'>
                            {' - ' + props.queryExecutionTimeMs + 'ms'}
                          </span>
                        )}
                        {(config.experimental?.showLastUpdatedOnDashboards && props.lastUpdatedAt) && (
                          <span className="text-gray-500 text-xs">
                            {' - ' + L.dashboard.updated + String(new Date(props.lastUpdatedAt).toLocaleTimeString([], { timeZoneName: 'short' }))}
                          </span>
                        )}
                      </div>
        
                      <div className="flex items-center space-x-4">
                        {Object.keys(filter).length > 0 && 
                                              <span className="mr-3 inline-flex items-center rounded-full border border-gray-200 bg-white py-1.5 pl-3 pr-2 text-sm font-medium text-gray-900">
                                                <span>{Object.keys(filter).length} {Object.keys(filter).length > 1 ? L.dashboard.filters_applied : L.dashboard.filter_applied} </span>
                                                <Button 
                                                  className="ml-2 inline-flex !h-7 !w-7 !p-1 flex-shrink-0 text-gray-500 hover:bg-gray-200 hover:text-gray-500"
                                                  shape="circle"
                                                  aria-label="Remove filters"
                                                  variant="unstyled"
                                                  icon={
                                                    <Icon 
                                                      className="stroke-[1.5]"
                                                      role="decorative"
                                                      name="XCircleIcon"
                                                    />
                                                  }
                                                  onClick={() => {
                                                    setFilter({});
                                                  }}
                                                />
                                              </span>
                        }
                        <div className="inline-flex items-center">
                          <Button
                            className="w-6 h-6 m-2 !p-0 text-gray-400 hover:text-gray-600 focus:text-gray-600"
                            aria-label="Search table"
                            variant="unstyled"
                            icon={<Icon
                              className="stroke-[1.5]"
                              role="decorative"
                              name="MagnifyingGlassIcon"
                            />}
                            onClick={() => setOpenSearch(true)}
                          />

                          {!props.component.hideTotalCount && <span className="mr-2">{L.dashboard.total_count + ':' + (props.records || []).length}</span>}
                        </div>
                      </div>
                    </div>
                  </th>
                </tr>) : null}
                {cols.length > 0 && <tr>
                  {cols.map((c, i) => {
                    if (c === 'uid' && props.component.hideUidCopyButton) return null;
                    if (tableWhenColumns.has(c)) {
                      return <th key={c} scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900 w-auto">
                        {localize(tableWhenColumns.get(c)!.columnHeader)}
                      </th>;
                    }
                    if (applicantCreatorButtonColumns.has(c)) {
                      return <th key={c} scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900 w-auto">
                        {localize(applicantCreatorButtonColumns.get(c)!.columnHeader)}
                      </th>;
                    }
                    if (noHeaderCols.includes(c)) {
                      return <th key={c}></th>;
                    }
                    const isSorted = sort?.key === c;
                    const arrowColor = isSorted ? "text-gray-900" : "text-gray-400";
                    return (
                      <th
                        key={c}
                        scope="col"
                        className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        <div className="flex items-center">
                          {(!superHeader && i === 0) && props.extra}
                          {snakeToEnglish(c)}
                          {isSorted && sort.order === 'descending' ? (
                            <Button
                              aria-label="Sort ascending"
                              variant="unstyled"
                              className={`w-4 h-4 ml-1 !p-0 ${arrowColor} hover:text-gray-600 focus:text-gray-600`}
                              icon={
                                <Icon
                                  className="stroke-[1.5]"
                                  role="decorative"
                                  name="ArrowUpIcon"
                                /> 
                              }
                              onClick={() => handleSort(c)}
                            />
                          ) : (
                            <Button 
                              aria-label="Sort descending"
                              variant="unstyled"
                              className={`w-4 h-4 ml-1 !p-0 ${arrowColor} hover:text-gray-600 focus:text-gray-600`}
                              icon={
                                <Icon 
                                  className="stroke-[1.5]"
                                  role="decorative"
                                  name="ArrowDownIcon"
                                />
                              }
                              onClick={() => handleSort(c)}
                            />
                          )}
                        </div>
                      </th>
                    );
                  })}
                  {/* Placeholder columns for view and PDF download buttons */}
                  {!(hideViewButton) && <th scope="col" className="py-3.5 px-3 text-center text-sm font-semibold text-gray-900 w-auto col">{resolvedLinkLabel || L.dashboard.view}</th>}
                  {includePdfDownload && <th scope="col" className="py-3.5 px-3 text-center text-sm font-semibold text-gray-900 w-auto" />}
                </tr>}
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {cols.length === 0
                  ? (<tr className={"bg-gray-50"}><td colSpan={3} className="px-3 py-4 text-sm text-gray-500 max-w-md break-word w-full">{L.dashboard.no_results}</td></tr>)
                  : currentPageRecords.map((r, i) => {
                    return <tr key={i} className={i % 2 === 0 ? "bg-gray-100" : "bg-gray-50"}>
                      {cols.map(c => {
                        const { content, JSONContent } = formatColumnValue(
                          r[c],
                          (url: string, index: number) => <ZoomableImage key={`${c}_${index}`} url={signedS3Urls[url] ?? url} fullScreenSrc={url} />,
                          columnToRenderOptionsMap[c.toLowerCase()]
                        );
                        if (c === 'uid') {
                          if (props.component.hideUidCopyButton) return null;
                          return <td key={c + '_copy_button'} className="px-3 w-20 py-4 text-sm text-gray-400 max-w-md break-word">
                            <Button
                              aria-label="Copy UID"
                              className="w-6 h-6 !p-0 hover:text-gray-600 focus:text-gray-600"
                              variant="unstyled"
                              icon={<Icon
                                className="stroke-[1.5]"
                                role="decorative"
                                name="Square2StackIcon"
                              />}
                              onClick={() => { copyToClipboard(r.uid); toast({ description: 'UID Copied', variant: 'success' });}}
                            />
                          </td>
                        }
                        if (c === 'full_uid') {
                          return <td key={c} className="px-3 py-4 text-sm text-gray-600 max-w-md break-word">
                            {r.uid}
                          </td>;
                        }
                        if(offlineStatusCols.includes(c.toLowerCase())) {
                          return <td key={c} className="px-3 w-[8em]">
                            <SyncBadge id={r.uid} />
                          </td>;
                        }
                        if (tableWhenColumns.has(c)) {
                          return <TableWhenCell
                            key={`${r.uid}-${c}`}
                            col={tableWhenColumns.get(c)!}
                            cellValue={r[c]}
                            uid={r.uid}
                            content={content}
                            JSONContent={JSONContent}
                            cellRenderer={cellRenderer}
                            localize={localize}
                          />;
                        }
                        const applicantCreatorButtonColumn = applicantCreatorButtonColumns.get(c);
                        if (applicantCreatorButtonColumn) {
                          return <ApplicantCreatorButtonComponent
                            key={`${r.uid}-${c}`}
                            uid={r.uid}
                            applicantCreatorTargetField={c}
                            existingLinkedUid={r[c]}
                            label={localize(applicantCreatorButtonColumn.label) ?? 'Create Applicant'}
                            linkTextPrefix={localize(applicantCreatorButtonColumn.linkTextPrefix) ?? undefined}
                          />;
                        }
                        // phone_number, email, and notif target field(s) are automatically added to the table SQL query if a notification reference column is included
                        if (notifColumns.includes(c)) {
                          if ((c.endsWith('_sms') && r['phone_number']) || (c.endsWith('_email') && r['email'])) {
                            return <NotifSendButtonComponent col={c} uid={r.uid} notifTargetField={c} prev={r[c]} />
                          }
                        }
                        
                        // LinkTo column
                        const colGens = linkGensByCol[c];
                        if (colGens?.length) {
                          const cellStr = typeof r[c] === 'string' ? r[c] : '';
                          const matched = cellStr ? colGens.find((g) => g.templateRegex.test(cellStr)) : undefined;
                          if (matched) {
                            return <td key={c} className="px-3 py-4 text-sm text-gray-600 max-w-md break-words">
                              <a href={cellStr} target='_blank' className="text-indigo-600 hover:text-indigo-900">
                                {localize(matched.linkTo.label) || L.dashboard.view}
                              </a>
                            </td>;
                          }
                        }
                        return <td key={c} className="px-3 py-4 text-sm text-gray-600 max-w-md break-words">
                          {cellRenderer ? cellRenderer(r[c]) : JSONContent ? <pre className="">{JSON.stringify(JSONContent, undefined, 2)}</pre> : <span>{content}</span>}
                        </td>
                      })}

                      {(!hideViewButton) && <td key='view' className="w-px whitespace-nowrap px-1 py-2 text-sm text-gray-600 text-center">
                        {r.uid && (() => {
                          return <a href={appPath + r.uid + (resolvedLinksToSection
                            ? (typeof resolvedLinksToSection === 'string'
                              ? '#' + encodeURIComponent(resolvedLinksToSection)
                              : '/' + resolvedLinksToSection.path) : '')} target='_blank' className="text-indigo-600 hover:text-indigo-900 mr-3 -ml-3">
                            {resolvedLinkLabel || L.dashboard.view}
                          </a>
                        })()}
                      </td>}
                      {includePdfDownload && <td key='pdf' className="px-1 py-2 text-sm text-gray-600 text-center">
                        {r[props.component.pdfDownload!.uidColumn ?? 'uid']
                          ? <Button
                            aria-label="Download PDF"
                            variant="secondary"
                            isDisabled={!!uidDownloadingPdf}
                            loading={uidDownloadingPdf === r[props.component.pdfDownload!.uidColumn ?? 'uid']}
                            onClick={async () => {
                              setUidDownloadingPdf(r.uid);
                              try {
                                const response = await createPdf({
                                  uid: r[props.component.pdfDownload!.uidColumn ?? 'uid'],
                                  subsurveyPath: props.component.pdfDownload!.subsurvey.path,
                                  lang: 'en',
                                });
                                if (response.error) {
                                  toast({ description: response.error, variant: 'error' });
                                  console.error(response.error);
                                } else {
                                  window.open(response.signedUrl, '_blank');
                                }
                              } catch (error: any) {
                                console.error(error);
                                toast({ description: 'message' in error ? error.message : 'Unknown error', variant: 'error' });
                              } finally {
                                setUidDownloadingPdf('');
                              }
                            }}
                          >
                            {pdfDownloadLabel}
                          </Button>
                          : <span className="text-gray-500">{L.dashboard.no_uid_for_this_row}</span>}
                      </td>}
                    </tr>
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {totalPages > 1 && (
        <div className="mt-4 flex justify-between items-center">
          <button
            className={`shadow inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              currentPage === 1
                ? "bg-gray-300 text-gray-500"
                : "bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            }`}
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            <ArrowLeftIcon className="w-5 h-5 mr-1" />
            {L.dashboard.previous_page}
          </button>
          <div className="text-gray-700">
            Page {currentPage} of {totalPages}
          </div>
          <button
            className={`shadow inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              currentPage === totalPages
                ? "bg-gray-300 text-gray-500"
                : "bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            }`}
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            {L.dashboard.next_page}
            <ArrowRightIcon className="w-5 h-5 ml-1" />
          </button>
        </div>
      )}
    </div>
  </OfflineSyncContext.Provider>
}
